import React, { useState } from 'react';
import { type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Link2,
  Image as ImageIcon,
  ShoppingBag,
  Check,
  X
} from 'lucide-react';
import ColorPickerPopover from './ColorPickerPopover';

export interface UniversalBubbleMenuProps {
  editor: Editor | null;
  allowHeadings?: boolean;
  allowLists?: boolean;
  allowQuote?: boolean;
  allowColors?: boolean;
  allowLink?: boolean;
  allowMedia?: boolean;
  allowProducts?: boolean;
  onOpenImageModal?: () => void;
  isImageModalOpen?: boolean;
  onOpenProductModal?: () => void;
  isProductModalOpen?: boolean;
  className?: string;
}

export default function UniversalBubbleMenu({
  editor,
  allowHeadings = false,
  allowLists = false,
  allowQuote = false,
  allowColors = true,
  allowLink = true,
  allowMedia = false,
  allowProducts = false,
  onOpenImageModal,
  isImageModalOpen = false,
  onOpenProductModal,
  isProductModalOpen = false,
  className = ''
}: UniversalBubbleMenuProps) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor) return null;

  // Don't render bubble menu if image or product modal is open
  if (isImageModalOpen || isProductModalOpen) return null;

  const handleOpenLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setShowLinkInput(true);
  };

  const handleSetLink = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      let finalUrl = linkUrl.trim();
      if (!/^https?:\/\//i.test(finalUrl) && !finalUrl.startsWith('/') && !finalUrl.startsWith('#')) {
        finalUrl = `https://${finalUrl}`;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: finalUrl }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  return (
    <BubbleMenu
      editor={editor}
      className={`bg-[#1C1817]/95 text-white border border-white/20 rounded-2xl shadow-2xl p-1 z-50 backdrop-blur-xl animate-in fade-in zoom-in-95 max-w-[calc(100vw-1.5rem)] ${className}`}
    >
      {showLinkInput ? (
        <form onSubmit={handleSetLink} className="flex items-center gap-1.5 px-1 py-0.5">
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            className="px-2.5 py-1 bg-white/10 text-white placeholder-white/40 text-xs rounded border border-white/20 outline-none w-44 sm:w-48 focus:border-white/50"
            autoFocus
          />
          <button
            type="submit"
            className="p-1 hover:bg-emerald-600 rounded text-emerald-400 hover:text-white transition-colors cursor-pointer shrink-0"
            title="Terapkan Link"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleRemoveLink}
            className="p-1 hover:bg-rose-600 rounded text-rose-400 hover:text-white transition-colors cursor-pointer shrink-0"
            title="Hapus Link"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </form>
      ) : (
        /* Scrollable container on mobile with touch-scroll & shrink-0 buttons */
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth touch-pan-x py-0.5 px-0.5 max-w-full">
          {/* Group 1: High Frequency Text Formatting (Always Upfront) */}
          {/* Bold */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
              editor.isActive('bold') ? 'bg-white/20 text-white font-bold' : 'text-white/80'
            }`}
            title="Tebal (Ctrl+B)"
          >
            <Bold className="w-3.5 h-3.5" />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
              editor.isActive('italic') ? 'bg-white/20 text-white' : 'text-white/80'
            }`}
            title="Miring (Ctrl+I)"
          >
            <Italic className="w-3.5 h-3.5" />
          </button>

          {/* Underline */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
              editor.isActive('underline') ? 'bg-white/20 text-white' : 'text-white/80'
            }`}
            title="Garis Bawah (Ctrl+U)"
          >
            <UnderlineIcon className="w-3.5 h-3.5" />
          </button>

          {/* Group 2: Color Picker Popover (High Priority - Immediately accessible) */}
          {allowColors && (
            <>
              <span className="w-[1px] h-3.5 bg-white/20 mx-0.5 shrink-0" />
              <ColorPickerPopover editor={editor} className="shrink-0" />
            </>
          )}

          {/* Group 3: Link */}
          {allowLink && (
            <button
              type="button"
              onClick={handleOpenLink}
              className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                editor.isActive('link') ? 'bg-white/20 text-white' : 'text-white/80'
              }`}
              title="Sisipkan Tautan URL"
            >
              <Link2 className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Group 4: Headings (H2, H3) */}
          {allowHeadings && (
            <>
              <span className="w-[1px] h-3.5 bg-white/20 mx-0.5 shrink-0" />
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                  editor.isActive('heading', { level: 2 }) ? 'bg-white/20 text-white font-bold' : 'text-white/80'
                }`}
                title="Sub-judul H2"
              >
                <Heading2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                  editor.isActive('heading', { level: 3 }) ? 'bg-white/20 text-white font-bold' : 'text-white/80'
                }`}
                title="Bagian H3"
              >
                <Heading3 className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {/* Group 5: Quote & Lists */}
          {(allowQuote || allowLists) && (
            <span className="w-[1px] h-3.5 bg-white/20 mx-0.5 shrink-0" />
          )}

          {allowQuote && (
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                editor.isActive('blockquote') ? 'bg-white/20 text-white' : 'text-white/80'
              }`}
              title="Kutipan"
            >
              <Quote className="w-3.5 h-3.5" />
            </button>
          )}

          {allowLists && (
            <>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                  editor.isActive('bulletList') ? 'bg-white/20 text-white' : 'text-white/80'
                }`}
                title="Daftar Poin"
              >
                <List className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                  editor.isActive('orderedList') ? 'bg-white/20 text-white' : 'text-white/80'
                }`}
                title="Daftar Angka"
              >
                <ListOrdered className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {/* Group 6: Media & Shoppable Product Links */}
          {(allowMedia || allowProducts) && (
            <span className="w-[1px] h-3.5 bg-white/20 mx-0.5 shrink-0" />
          )}

          {allowMedia && onOpenImageModal && (
            <button
              type="button"
              onClick={onOpenImageModal}
              className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors cursor-pointer shrink-0"
              title="Sisipkan Gambar (Hostinger CDN)"
            >
              <ImageIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {allowProducts && onOpenProductModal && (
            <button
              type="button"
              onClick={onOpenProductModal}
              className={`p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer shrink-0 ${
                editor.getAttributes('link')?.['data-product-id']
                  ? 'bg-brand-wine text-white'
                  : 'text-white/80 hover:text-white'
              }`}
              title="Tautkan Produk Toko (Shoppable Look)"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </BubbleMenu>
  );
}
